import { useState } from "react";
import { useWizard } from "@/stores/wizard";
import { useAuthStore } from "@/stores/auth";
import { api, ApiError } from "@/lib/api";
import { LabeledInput } from "./LabeledInput";

/**
 * Wizard step 1 — name / symbol / description / cover (+ banner). Image
 * uploads go through the Pinata proxy and require SIWE; the user can fill
 * the form first and sign in later if they want.
 */
export function Step1Info() {
  const w = useWizard();
  const isAuthed = useAuthStore((s) => s.isAuthed());
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const upload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "imageCid" | "bannerCid",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const { cid } = await api.uploadFile(file);
      w.set(field, cid);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUploadError("Connect your wallet and sign in to upload images.");
      } else {
        setUploadError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <LabeledInput
        label="Name"
        value={w.name}
        onChange={(v) => w.set("name", v)}
        placeholder="My Collection"
      />
      <LabeledInput
        label="Symbol"
        value={w.symbol}
        onChange={(v) => w.set("symbol", v.toUpperCase())}
        placeholder="MYC"
      />
      <label className="flex flex-col gap-1">
        <span className="label">Description</span>
        <textarea
          className="input min-h-[100px]"
          value={w.description}
          onChange={(e) => w.set("description", e.target.value)}
        />
      </label>
      {!isAuthed && (
        <div className="warning p-3 text-xs">
          Image uploads require a wallet connection — the Pinata proxy is sign-in protected.
          You can keep filling the form now and sign in later when you hit Deploy.
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="label">{w.kind === "badge" ? "Badge artwork" : "Cover image"}</span>
          <input
            type="file"
            accept="image/*"
            disabled={!isAuthed}
            onChange={(e) => void upload(e, "imageCid")}
          />
          {w.imageCid && <span className="text-xs text-muted-foreground">cid: {w.imageCid}</span>}
        </label>
        {w.kind !== "badge" && (
          <label className="flex flex-col gap-1">
            <span className="label">Banner (optional)</span>
            <input
              type="file"
              accept="image/*"
              disabled={!isAuthed}
              onChange={(e) => void upload(e, "bannerCid")}
            />
            {w.bannerCid && <span className="text-xs text-muted-foreground">cid: {w.bannerCid}</span>}
          </label>
        )}
      </div>
      {uploading && <div className="text-sm text-muted-foreground">Uploading to IPFS…</div>}
      {uploadError && <div className="text-sm text-red-600">{uploadError}</div>}
    </div>
  );
}
